import { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, UserRound, X } from "lucide-react";
import { Logo } from "@/components/site/Logo";
import { useSite } from "@/context/SiteContext";
import { useAuth, isStaff } from "@/context/AuthContext";

export const NAV = [
  ["Home", "/"],
  ["About", "/about"],
  ["Board", "/board"],
  ["Tryouts", "/tryouts"],
  ["Teams", "/teams"],
  ["Athletes", "/athletes"],
  ["Events", "/events"],
  ["Media", "/media"],
  ["Sponsors", "/sponsors"],
  ["Parents", "/parents"],
  ["Contact", "/contact"],
];

const Ticker = () => {
  const { settings } = useSite();
  const text = settings.ticker_text || "DIFFERENT PARKS. ONE COAST. ONE MISSION.";
  const items = Array.from({ length: 6 }, () => text);
  return (
    <div className="bg-gold text-navy overflow-hidden" data-testid="announcement-ticker">
      <div className="flex w-max animate-marquee whitespace-nowrap py-1.5">
        {[...items, ...items].map((t, i) => (
          <span key={i} className="mx-6 font-display text-sm font-extrabold uppercase tracking-wider">
            {t} <span className="mx-4">★</span>
          </span>
        ))}
      </div>
    </div>
  );
};

const AccountLink = ({ onClick }) => {
  const { user } = useAuth();
  const to = isStaff(user) ? "/admin" : user ? "/portal" : "/login";
  const label = isStaff(user) ? "Dashboard" : user ? "Portal" : "Login";
  return (
    <Link to={to} onClick={onClick} className="inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-slate-200 hover:text-gold transition-colors" data-testid="nav-account-link">
      <UserRound className="h-4 w-4" /> {label}
    </Link>
  );
};

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const linkCls = ({ isActive }) =>
    `relative px-1 py-2 text-[13px] font-semibold uppercase tracking-wider transition-colors ${isActive ? "text-gold" : "text-slate-200 hover:text-white"}`;
  return (
    <header className="fixed inset-x-0 top-0 z-50" data-testid="site-header">
      <Ticker />
      <div className="bg-navy/85 backdrop-blur-xl border-b border-white/10">
        <div className="wrap flex h-[72px] items-center justify-between gap-6">
          <Logo />
          <nav className="hidden xl:flex items-center gap-5" aria-label="Main">
            {NAV.map(([label, to]) => (
              <NavLink key={to} to={to} end={to === "/"} className={linkCls} data-testid={`nav-link-${label.toLowerCase()}`}>
                {label}
              </NavLink>
            ))}
          </nav>
          <div className="hidden xl:flex items-center gap-5">
            <AccountLink />
            <Link to="/tryouts" className="btn-gold !px-5 !py-2.5 !text-sm" data-testid="nav-register-button">Register</Link>
          </div>
          <button className="xl:hidden p-2 text-white" onClick={() => setOpen(!open)} aria-label="Toggle menu" data-testid="mobile-menu-toggle">
            {open ? <X className="h-7 w-7" /> : <Menu className="h-7 w-7" />}
          </button>
        </div>
      </div>
      {open && (
        <div className="xl:hidden bg-pitch/97 backdrop-blur-xl border-b border-line h-[calc(100svh-104px)] overflow-y-auto" data-testid="mobile-menu">
          <nav className="wrap py-6 flex flex-col">
            {NAV.map(([label, to]) => (
              <Link key={to} to={to} onClick={() => setOpen(false)} data-testid={`mobile-nav-link-${label.toLowerCase()}`}
                className={`border-b border-line/60 py-4 font-display text-3xl font-black uppercase italic ${pathname === to ? "text-gold" : "text-white"}`}>
                {label}
              </Link>
            ))}
            <div className="mt-8 flex items-center justify-between">
              <AccountLink onClick={() => setOpen(false)} />
              <Link to="/tryouts" onClick={() => setOpen(false)} className="btn-gold" data-testid="mobile-register-button">Register</Link>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
};
